import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Box, Button, CircularProgress, Typography } from '@mui/material'
import PropTypes from 'prop-types'

import {
    quoteAttachmentsApi,
    quoteAttachmentsPath,
    useDeleteQuoteAttachmentMutation,
    useGetQuoteAttachmentsQuery,
    useSetQuoteAttachmentVisibilityMutation,
} from '@/app/services/quoteAttachments'
import { uploadAttachmentFile } from '@/app/services/uploadAttachmentFile'

import AttachmentsPanel from './AttachmentsPanel'

// Download URIs are 15-minute SAS links minted when the quote is fetched. Past this age,
// refetch before opening so the user never lands on an expired link.
const DOWNLOAD_URI_MAX_AGE_MS = 14 * 60 * 1000

/**
 * QuoteAttachmentsPanel - The documents ledger for a quote. Rows come from the quote payload
 * through the panel's own cache entry; every write invalidates that entry so the refetch redraws
 * them. Rendering is gated by the caller.
 *
 * @param {Object} props
 * @param {string} props.organisationId
 * @param {string} props.quoteId
 */
const QuoteAttachmentsPanel = ({ organisationId, quoteId }) => {
    const { t } = useTranslation()
    const dispatch = useDispatch()

    const { data, fulfilledTimeStamp, isError, isLoading, refetch } = useGetQuoteAttachmentsQuery({
        organisationId,
        quoteId,
    })
    // No polling: a pending row shows its scan result after a reload or the panel's next write.
    const attachments = data ?? []

    const [setVisibility] = useSetQuoteAttachmentVisibilityMutation()
    const [deleteAttachment, { isLoading: isDeleting }] = useDeleteQuoteAttachmentMutation()

    const resolveDownloadUri = async (attachment) => {
        if (fulfilledTimeStamp && Date.now() - fulfilledTimeStamp <= DOWNLOAD_URI_MAX_AGE_MS) {
            return attachment.downloadUri
        }
        // A failed refetch still resolves with the last good rows, and their links are the
        // expired ones. Report the error instead of opening one.
        const { data: freshAttachments, error } = await refetch()
        if (error) throw error
        const freshAttachment = freshAttachments?.find(({ documentId }) => documentId === attachment.documentId)
        if (!freshAttachment) throw { data: t('This attachment is no longer on this quote.') }
        return freshAttachment.downloadUri
    }

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress
                    aria-label={t('Loading attachments')}
                    color="secondary"
                />
            </Box>
        )
    }

    // Without rows the ledger would claim the quote has no documents, and offer free slots it may
    // not have. Say what happened instead.
    if (isError && !data) {
        return (
            <Box sx={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 2, mx: 1.5, py: 2 }}>
                <Typography color="error">{t('Could not load the attachments.')}</Typography>
                <Button
                    size="small"
                    variant="outlined"
                    onClick={refetch}
                >
                    {t('Try again')}
                </Button>
            </Box>
        )
    }

    return (
        <AttachmentsPanel
            attachments={attachments}
            emptyMessage={t('No attachments on this $t(quote) yet.')}
            isDeleting={isDeleting}
            organisationId={organisationId}
            resolveDownloadUri={resolveDownloadUri}
            title={t('$t(Quote) attachments')}
            onDelete={(documentId) => deleteAttachment({ organisationId, quoteId, documentId }).unwrap()}
            onSetVisibility={(documentId, isPublic) =>
                setVisibility({ organisationId, quoteId, documentId, isPublic }).unwrap()
            }
            onUploadFile={(file, options) =>
                uploadAttachmentFile({ path: quoteAttachmentsPath({ organisationId, quoteId }), file, ...options })
            }
            onUploadsSettled={() =>
                dispatch(
                    quoteAttachmentsApi.endpoints.getQuoteAttachments.initiate(
                        { organisationId, quoteId },
                        { forceRefetch: true, subscribe: false }
                    )
                )
            }
        />
    )
}

QuoteAttachmentsPanel.propTypes = {
    organisationId: PropTypes.string.isRequired,
    quoteId: PropTypes.string.isRequired,
}

export default QuoteAttachmentsPanel
